import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';

const UserSearchSelect = ({ value, onChange, placeholder = "Search users..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load all users initially
  useEffect(() => {
    const loadAllUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get('/users/search', {
          params: { q: '', all: true }
        });
        setAllUsers(response.data);
        setUsers(response.data);
      } catch (error) {
        console.error('Error loading users:', error);
        setAllUsers([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadAllUsers();
  }, []);

  // Search users
  useEffect(() => {
    if (searchTerm.length === 0) {
      setUsers(allUsers);
      return;
    }

    if (searchTerm.length < 2) {
      return;
    }

    setLoading(true);
    const searchUsers = async () => {
      try {
        const response = await api.get('/users/search', {
          params: { q: searchTerm }
        });
        setUsers(response.data);
      } catch (error) {
        console.error('Error searching users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, allUsers]);

  const handleSelect = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.name);
    setShowDropdown(false);
    onChange(user.id);
  };

  const handleClear = () => {
    setSelectedUser(null);
    setSearchTerm('');
    setUsers(allUsers);
    onChange('');
  };

  const handleFocus = () => {
    setShowDropdown(true);
    if (searchTerm.length === 0) {
      setUsers(allUsers);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="w-full pl-10 pr-20 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {selectedUser && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="text-gray-400 hover:text-gray-600"
          >
            <ChevronDownIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Loading...
            </div>
          ) : users.length > 0 ? (
            <>
              {searchTerm.length === 0 && (
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                  All Users ({users.length})
                </div>
              )}
              {users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className={`w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors ${
                    selectedUser?.id === user.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900">{user.name}</div>
                  <div className="text-sm text-gray-500">{user.email}</div>
                </button>
              ))}
            </>
          ) : searchTerm.length >= 2 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No users found
            </div>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              Type to search users
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchSelect;
